<template>
    <div class="article-production-site-allocator mb-4">
        <div class="row mb-2">
            <label class="col-xl-3"></label>
            <div class="col-xl-3 pull-right col-form-label">
                <h3 class="kt-section__title kt-section__title-sm mb-2">
                    {{ translations.usesResources.usesResources }}
                </h3>
            </div>

            <div class="col-xl-3 pull-right mb-4">
                <button @click="addArticleResource" type="button" class="btn pull-right btn-success">
                    <i class="la la-plus"></i>{{ translations.usesResources.resource }}
                </button>
            </div>
        </div>

        <div class="resources-container">
            <ArticleResourceListRow
                @onRemoveArticleResource="removeArticleResource"
                v-for="resource in resources"
                :key="resource.id"
                :ref="(el) => setRowRef(el, resource.id)"
                :resource="resource"
            />

            <div v-if="resources.length === 0">
                <div class="nodata" style="padding: 10px 0; color: #7f849a; text-align: center">
                    {{ translations.usesResources.noResourcesAssigned }}
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import type { IArticleResourceToCreateOrUpdate } from "@/data/model/Article";
    import type { ArticleResourcesFormGetValidatedDataResult } from "@/portal/components/article/ArticleResourcesFormGetValidatedDataResult";
    import { translations } from "@/shared/localization/translations";
    import { v4 as uuidv4 } from "uuid";
    import { ref } from "vue";

    import ArticleResourceListRow from "@/portal/components/article/ArticleResourceListRow.vue";

    const resources = ref<IArticleResourceToCreateOrUpdate[]>([]);
    const rowRefs = ref<Record<string, typeof ArticleResourceListRow>>({});

    function removeArticleResource(resource: IArticleResourceToCreateOrUpdate) {
        resources.value = resources.value.filter((r) => r.id !== resource.id);
    }

    function setRowRef(row, id) {
        if (row) {
            rowRefs.value[id] = row;
        } else {
            delete rowRefs.value[id];
        }
    }

    function addArticleResource() {
        resources.value.push({
            id: uuidv4(),
            resourceId: undefined,
            numberOfUnits: undefined,
        });
    }

    function getValidatedData(): ArticleResourcesFormGetValidatedDataResult {
        if (isDataValid()) {
            return {
                isValid: true,
                articleResources: resources.value!.map((r) => {
                    return {
                        id: r.id,
                        resourceId: r.resourceId!,
                        numberOfUnits: r.numberOfUnits!,
                    };
                }),
            };
        }

        // data is invalid, return empty array
        return {
            isValid: false,
            articleResources: [],
        };
    }

    function setData(resourcesToSet: IArticleResourceToCreateOrUpdate[]) {
        resources.value = resourcesToSet;
    }

    function isDataValid(): boolean {
        let hasErrors = false;

        Object.values(rowRefs.value).forEach((r) => {
            if (r.validate() === false) {
                hasErrors = true;
            }
        });

        return !hasErrors;
    }

    defineExpose({
        isDataValid,
        getValidatedData,
        setData,
    });
</script>

<style scoped lang="scss"></style>
